/**
 * Parsing tolerante de valores crudos de RTDB/REST.
 *
 * Port bit a bit de `rider_app/lib/core/utils/parsing.dart`. El RTDB compartido
 * tolera coordenadas como number | int | string y duraciones como "30s" o 30.
 *
 * Replica la semántica ESTRICTA de `double.tryParse`/`int.tryParse` de Dart:
 * la cadena completa debe ser numérica (a diferencia de `parseFloat`, que hace
 * parseo parcial: `parseFloat('12abc') === 12`). Por eso se usa `Number()`, que
 * devuelve `NaN` ante restos no numéricos, igual que Dart devuelve `null`.
 */

/** Equivalente a `double.tryParse(s)` de Dart sobre una cadena ya recortada. */
function tryParseDartDouble(trimmed: string): number | null {
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isNaN(n) ? null : n;
}

/** Equivalente a `int.tryParse(s)` (radix 10) de Dart sobre una cadena recortada. */
function tryParseDartInt(trimmed: string): number | null {
  // Dart int.tryParse acepta solo signo opcional + dígitos (sin decimales).
  if (!/^[+-]?\d+$/.test(trimmed)) return null;
  const n = Number.parseInt(trimmed, 10);
  return Number.isNaN(n) ? null : n;
}

/** Trunca hacia cero, como `num.toInt()` de Dart. */
function toIntDart(n: number): number {
  return Math.trunc(n);
}

/**
 * Coordenada como `double?`. Acepta number, o string que represente un número
 * completo. Devuelve `null` para null/vacío/no-numérico.
 */
export function parseCoord(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    return tryParseDartDouble(trimmed);
  }
  return null;
}

/**
 * Entero flexible. Acepta number (truncado), o string entera; si la string es
 * decimal ("1.5") cae a double y trunca (→1), igual que el Dart original.
 */
export function parseIntFlexible(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return toIntDart(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const asInt = tryParseDartInt(trimmed);
    if (asInt !== null) return asInt;
    const asDouble = tryParseDartDouble(trimmed);
    return asDouble === null ? null : toIntDart(asDouble);
  }
  return null;
}

/**
 * Duración en segundos. Acepta number, o string tipo "30s"/"30"/"1.5s"
 * (elimina TODAS las 's'). Fallback a 0 (nunca null), como el Dart original.
 */
export function parseDurationSeconds(value: unknown): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return toIntDart(value);
  if (typeof value === 'string') {
    const cleaned = value.trim().replace(/s/g, '');
    const asInt = tryParseDartInt(cleaned);
    if (asInt !== null) return asInt;
    const asDouble = tryParseDartDouble(cleaned);
    return asDouble === null ? 0 : toIntDart(asDouble);
  }
  return 0;
}
